import { useState, useRef, useCallback, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Play, RefreshCw, Download, Trophy, Scale } from "lucide-react";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, ReferenceLine, Cell,
} from "recharts";

const MONO = { fontFamily: "JetBrains Mono, monospace" };
const A_COLOR = "#3B82F6";   // P(NT) Regime-Conditioned
const B_COLOR = "#F59E0B";   // Fixed Delta
const REGIME_COLORS = {
  "Quiet Range": "#3B82F6", "Bullish Trend": "#10B981",
  "Bearish Trend": "#EF4444", Volatile: "#F59E0B",
};

const WING_OPTIONS = [5, 10, 15, 20];
const SIM_OPTIONS = [1000, 5000, 10000];
const LOOKBACK_OPTIONS = [{ v: 1, l: "1 yr" }, { v: 2, l: "2 yr" }, { v: 3, l: "3 yr" }];
const DTE_OPTIONS = [7, 14, 21, 35, 45];
const EXIT_OPTIONS = [
  { v: "hold_to_expiry", l: "Hold to expiry" },
  { v: "profit_50", l: "50% profit target" },
  { v: "profit_25", l: "25% profit target" },
  { v: "dte_21", l: "21 DTE (time exit)" },
  { v: "profit_50_or_21", l: "50% profit OR 21 DTE" },
];
const cyclesPerYear = (dte) => Math.max(1, Math.round(252 / dte));
// P(NT) threshold zone colour: 25-45 red, 46-65 amber, 66-85 green
const thresholdColor = (v) => (v <= 45 ? "#EF4444" : v <= 65 ? "#F59E0B" : "#10B981");

function money(v) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return "—";
  const n = Number(v);
  const s = n < 0 ? "-" : "";
  const a = Math.abs(n);
  return a >= 1000 ? `${s}$${(a / 1000).toFixed(1)}k` : `${s}$${a.toFixed(0)}`;
}
const pct = (v) => (v === null || v === undefined ? "—" : `${Number(v).toFixed(1)}%`);

// Range slider with inline value readout
function Slider({ label, value, min, max, step, onChange, testId, fmt, color }) {
  return (
    <div>
      <div className="flex justify-between items-baseline mb-1">
        <Label className="text-xs" style={{ color: "var(--hoip-text-secondary)" }}>{label}</Label>
        <span className="text-xs font-medium" style={{ ...MONO, color: color || "var(--hoip-text-primary)" }}>{fmt ? fmt(value) : value}</span>
      </div>
      <input type="range" data-testid={testId} value={value} min={min} max={max} step={step}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
        style={{ background: "var(--hoip-hover)", accentColor: color || A_COLOR }} />
    </div>
  );
}

function NativeSelect({ value, onChange, options, testId, label }) {
  return (
    <div>
      {label && <Label className="text-xs mb-1 block" style={{ color: "var(--hoip-text-secondary)" }}>{label}</Label>}
      <select data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full h-8 px-2 text-xs rounded-sm border outline-none appearance-none cursor-pointer"
        style={{ ...MONO, background: "var(--hoip-bg)", borderColor: "var(--hoip-border)", color: "var(--hoip-text-primary)" }}>
        {options.map(o => (
          <option key={o.value} value={o.value} style={{ background: "var(--hoip-card)", color: "var(--hoip-text-primary)" }}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

// Metric row: green = better, red = worse
function MetricRow({ label, a, b, higherBetter = true, fmt = (x) => x, help }) {
  const an = Number(a), bn = Number(b);
  let aBetter = null;
  if (Number.isFinite(an) && Number.isFinite(bn) && an !== bn) {
    aBetter = higherBetter ? an > bn : an < bn;
  }
  const cls = (isA) => {
    if (aBetter === null) return "var(--hoip-text-primary)";
    const better = isA ? aBetter : !aBetter;
    return better ? "#10B981" : "#EF4444";
  };
  return (
    <tr className="border-b" style={{ borderColor: "var(--hoip-divider)" }}>
      <td className="py-1.5 pr-2 text-xs" style={{ color: "var(--hoip-text-secondary)" }} title={help}>{label}</td>
      <td className="py-1.5 px-2 text-right text-xs font-medium" style={{ ...MONO, color: cls(true) }}>{fmt(a)}</td>
      <td className="py-1.5 pl-2 text-right text-xs font-medium" style={{ ...MONO, color: cls(false) }}>{fmt(b)}</td>
    </tr>
  );
}

function ChartCard({ title, subtitle, children, testId }) {
  return (
    <div className="border p-3 rounded-sm" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }} data-testid={testId}>
      <h3 className="text-xs font-bold mb-0.5" style={{ fontFamily: "Chivo, sans-serif" }}>{title}</h3>
      {subtitle && <p className="text-[10px] mb-2" style={{ color: "var(--hoip-text-tertiary)" }}>{subtitle}</p>}
      {children}
    </div>
  );
}

// ── Per-regime breakdown (Steps 4-5,8-9) ───────────────────────────────────
const REGIME_ORDER = ["Quiet Range", "Bullish Trend", "Bearish Trend", "Volatile"];
const shortRegime = (r) => (r || "").replace(" Trend", "").replace(" Range", "");

// Row highlight: green EV>0 · amber EV<0 & win>85 · red EV<0 & win<=85
function regimeTone(ev, win) {
  if (ev > 0) return { color: "#10B981", bg: "rgba(16,185,129,0.10)" };
  if (win > 85) return { color: "#F59E0B", bg: "rgba(245,158,11,0.10)" };
  return { color: "#EF4444", bg: "rgba(239,68,68,0.10)" };
}
// Credit/width annotation: ≥25 green · 10-24 amber · <10 muted (far-OTM expected)
function creditTone(pct) {
  if (pct >= 25) return "#10B981";
  if (pct >= 10) return "#F59E0B";
  return "var(--hoip-text-tertiary)";
}

function RegimeStrategyTable({ rows, strat, blended, color, label, testId }) {
  const pre = strat; // 'a' | 'b'
  const scored = rows.filter(r => (r[`${pre}_cycles`] || 0) >= 3);
  return (
    <div className="border rounded-sm overflow-hidden" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }} data-testid={testId}>
      <div className="px-3 py-2 text-[11px] font-bold border-b" style={{ color, borderColor: "var(--hoip-border)" }}>{label}</div>
      <table className="w-full">
        <thead>
          <tr className="border-b" style={{ borderColor: "var(--hoip-divider)" }}>
            {["Regime", "Win%", "Assign%", "Credit/W", "Cycles"].map((h, i) => (
              <th key={h} className={`text-[9px] font-medium py-1.5 px-2 ${i === 0 ? "text-left" : "text-right"}`} style={{ color: "var(--hoip-text-tertiary)" }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {REGIME_ORDER.map(rname => {
            const r = rows.find(x => x.regime === rname);
            const cyc = r ? (r[`${pre}_cycles`] || 0) : 0;
            if (!r || cyc < 3) {
              return <tr key={rname} data-testid={`regime-row-${strat}-${shortRegime(rname)}`}>
                <td className="text-left text-[10px] py-1.5 px-2" style={{ color: "var(--hoip-text-secondary)" }}>{shortRegime(rname)}</td>
                <td colSpan={4} className="text-right text-[10px] py-1.5 px-2 italic" style={{ color: "var(--hoip-text-tertiary)" }}>Insufficient data ({cyc} cycles)</td>
              </tr>;
            }
            const ev = r[`${pre}_ev`], win = r[`${pre}_win_rate`], crp = r[`${pre}_credit_pct`];
            const tone = regimeTone(ev, win);
            return (
              <tr key={rname} style={{ background: tone.bg }} data-testid={`regime-row-${strat}-${shortRegime(rname)}`}>
                <td className="text-left text-[10px] py-1.5 px-2 font-medium" style={{ color: "var(--hoip-text-primary)" }}>{shortRegime(rname)}</td>
                <td className="text-right text-[10px] py-1.5 px-2" style={MONO}>{win?.toFixed(1)}%</td>
                <td className="text-right text-[10px] py-1.5 px-2" style={MONO}>{r[`${pre}_assign_pct`]?.toFixed(1)}%</td>
                <td className="text-right text-[10px] py-1.5 px-2" style={{ ...MONO, color: creditTone(crp) }} title={crp < 10 ? "Far-OTM — low credit/width is expected for P(NT)" : ""}>{crp?.toFixed(0)}%</td>
                <td className="text-right text-[10px] py-1.5 px-2" style={{ ...MONO, color: "var(--hoip-text-tertiary)" }}>{cyc.toLocaleString()}</td>
              </tr>
            );
          })}
          <tr className="border-t font-semibold" style={{ borderColor: "var(--hoip-border)" }}>
            <td className="text-left text-[10px] py-1.5 px-2" style={{ color: "var(--hoip-text-secondary)" }}>All regimes</td>
            <td className="text-right text-[10px] py-1.5 px-2" style={MONO}>{blended?.win_rate?.toFixed(1)}%</td>
            <td className="text-right text-[10px] py-1.5 px-2" style={MONO}>{blended?.assign_pct?.toFixed(1)}%</td>
            <td className="text-right text-[10px] py-1.5 px-2" style={{ ...MONO, color: "var(--hoip-text-tertiary)" }}>—</td>
            <td className="text-right text-[10px] py-1.5 px-2" style={{ ...MONO, color: "var(--hoip-text-tertiary)" }}>{blended?.cycles?.toLocaleString()}</td>
          </tr>
        </tbody>
      </table>
      {scored.length === 0 && <div className="px-3 py-2 text-[10px] italic" style={{ color: "var(--hoip-text-tertiary)" }}>No regime has ≥3 cycles at these params.</div>}
    </div>
  );
}

function InsightPanel({ result, ticker }) {
  const rows = result.by_regime || [];
  const cell = (rname) => rows.find(r => r.regime === rname);
  const min5 = (rname) => (cell(rname)?.a_cycles || 0) >= 5;
  const lines = [];
  const tk = ticker.toUpperCase();
  // Line 1 — best regime
  if (result.best_regime && min5(result.best_regime)) {
    const c = cell(result.best_regime);
    lines.push({ icon: "✓", color: "#10B981", text: `Best entry regime for ${tk} P(NT): ${result.best_regime} — EV +$${c.a_ev?.toFixed(0)}/cycle, Win ${c.a_win_rate?.toFixed(0)}%, ${c.a_cycles.toLocaleString()} cycles` });
  }
  // Line 2 — regime to avoid (red)
  const red = REGIME_ORDER.map(cell).find(c => c && c.a_cycles >= 5 && c.a_ev < 0 && c.a_win_rate <= 85);
  if (red) lines.push({ icon: "✗", color: "#EF4444", text: `Avoid entering in ${red.regime} — negative EV (−$${Math.abs(red.a_ev).toFixed(0)}/cycle) across ${red.a_cycles.toLocaleString()} cycles.` });
  // Line 3 — surprising edge
  if (result.surprising_edge_regime && min5(result.surprising_edge_regime)) {
    const sr = result.surprising_edge_regime;
    const srA = cell(sr).a_assign_pct;
    const safer = REGIME_ORDER.slice(0, REGIME_ORDER.indexOf(sr)).map(cell).filter(c => c && c.a_cycles >= 5 && c.a_assign_pct > srA).sort((x, y) => y.a_assign_pct - x.a_assign_pct)[0];
    if (safer) lines.push({ icon: "!", color: "#3B82F6", text: `Unexpected: ${shortRegime(sr)} shows lower assignment risk (${srA?.toFixed(1)}%) than ${shortRegime(safer.regime)} (${safer.a_assign_pct?.toFixed(1)}%) on ${tk} — verify regime filter is active before entry.` });
  }
  // Line 4 — strategy winner in best regime
  const brw = result.best_regime_winner;
  if (brw) {
    const wlabel = brw.winner === "a" ? "P(NT)" : "Fixed Δ";
    lines.push({ icon: "★", color: brw.winner === "a" ? A_COLOR : B_COLOR, text: `In ${shortRegime(brw.regime)}: ${wlabel} wins on risk-adjusted return (Sortino ${brw.a_sortino} vs ${brw.b_sortino})` });
  }
  if (!lines.length) return null;
  return (
    <div className="border rounded-sm p-3 space-y-1.5" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }} data-testid="regime-insight-panel">
      <div className="text-[10px] font-semibold uppercase tracking-wide mb-1" style={{ color: "var(--hoip-text-tertiary)" }}>Auto-generated regime insights</div>
      {lines.map((l, i) => (
        <div key={i} className="flex items-start gap-2 text-[11px]" data-testid={`insight-line-${i + 1}`}>
          <span className="font-bold" style={{ color: l.color }}>{l.icon}</span>
          <span style={{ color: "var(--hoip-text-secondary)" }}>{l.text}</span>
        </div>
      ))}
    </div>
  );
}

function RegimeBreakdown({ result, ticker }) {
  if (!result?.by_regime?.length) return null;
  const bg = result.blended_gated || {};
  const aBlendNeg = (bg.a?.ev_per_cycle || 0) < 0;
  const bBlendNeg = (bg.b?.ev_per_cycle || 0) < 0;
  return (
    <div className="space-y-3" data-testid="regime-breakdown">
      {/* Simulation conditions badge (Step 8) */}
      <div className="text-[10px] px-3 py-1.5 rounded-sm border flex items-center gap-2" data-testid="sim-conditions-badge"
        style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-divider)", color: "var(--hoip-text-tertiary)", ...MONO }}>
        Simulation: all regimes · regime-labeled entries · {result.lab_params?.lookback || 2}yr lookback · {result.sim_count?.toLocaleString()} paths · credit annotated (not gated)
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
        <RegimeStrategyTable rows={result.by_regime} strat="a" blended={bg.a} color={A_COLOR} label="A · P(NT) Regime — per-regime edge" testId="regime-table-a" />
        <RegimeStrategyTable rows={result.by_regime} strat="b" blended={bg.b} color={B_COLOR} label="B · Fixed Delta — per-regime edge" testId="regime-table-b" />
      </div>
      {result.far_otm_note && (
        <div className="text-[10px] px-3 py-1.5 rounded-sm" data-testid="far-otm-note" style={{ background: "rgba(59,130,246,0.08)", color: "#3B82F6" }}>
          P(NT) spreads are structurally far-OTM — low credit/width ratio is expected. Edge comes from win rate, not premium size.
        </div>
      )}
      {result.risk_adjusted_split && (
        <div className="border rounded-sm p-3" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }} data-testid="risk-adjusted-split">
          <div className="text-[10px] font-semibold uppercase tracking-wide mb-2" style={{ color: "var(--hoip-text-tertiary)" }}>P(NT) risk-adjusted · All vs Best/Worst regime</div>
          <div className="grid grid-cols-3 gap-2">
            {[["All regimes", result.risk_adjusted_split.all && { sortino: result.risk_adjusted_split.all.sortino, sharpe: result.risk_adjusted_split.all.sharpe, profit_factor: result.risk_adjusted_split.all.profit_factor, regime: null }],
              ["Best regime", result.risk_adjusted_split.best_regime],
              ["Worst regime", result.risk_adjusted_split.worst_regime]].map(([lbl, d], i) => (
              <div key={lbl} className="rounded-sm p-2 border" style={{ borderColor: "var(--hoip-divider)" }} data-testid={`ra-col-${i}`}>
                <div className="text-[9px] uppercase tracking-wide" style={{ color: "var(--hoip-text-tertiary)" }}>{lbl}{d?.regime ? ` · ${shortRegime(d.regime)}` : ""}</div>
                {d ? <div className="mt-1 space-y-0.5 text-[10px]" style={MONO}>
                  <div className="flex justify-between"><span style={{ color: "var(--hoip-text-tertiary)" }}>Sortino</span><span style={{ color: (d.sortino || 0) >= 0 ? "#10B981" : "#EF4444" }}>{d.sortino}</span></div>
                  <div className="flex justify-between"><span style={{ color: "var(--hoip-text-tertiary)" }}>Sharpe</span><span>{d.sharpe}</span></div>
                  <div className="flex justify-between"><span style={{ color: "var(--hoip-text-tertiary)" }}>Profit factor</span><span>{d.profit_factor}</span></div>
                </div> : <div className="mt-1 text-[10px] italic" style={{ color: "var(--hoip-text-tertiary)" }}>Insufficient data</div>}
              </div>
            ))}
          </div>
        </div>
      )}
      <InsightPanel result={result} ticker={ticker} />
      {aBlendNeg && bBlendNeg && (
        <div className="text-[11px] px-3 py-2 rounded-sm border" data-testid="negative-ev-note" style={{ background: "#F59E0B14", borderColor: "#F59E0B", color: "#F59E0B" }}>
          Negative blended EV may reflect unfavorable regime cycles included in the simulation. Check the per-regime table — if EV is positive in Quiet Range or Bullish Trend rows, this strategy has edge when entered with the Screener&apos;s regime filter active.
        </div>
      )}
    </div>
  );
}

export default function StrikePhilosophy() {
  const [ticker, setTicker] = useState("UBER");
  const [dte, setDte] = useState(35);
  const [pntThreshold, setPntThreshold] = useState(70);
  const [shortDelta, setShortDelta] = useState(0.25);
  const [longDelta, setLongDelta] = useState(0.10);
  const [wingWidth, setWingWidth] = useState(10);
  const [exitRule, setExitRule] = useState("hold_to_expiry");
  const [normalize, setNormalize] = useState(false);
  const [simCount, setSimCount] = useState(10000);
  const [lookback, setLookback] = useState(2);
  const [fairWing, setFairWing] = useState(true);

  const [jobId, setJobId] = useState(null);
  const [status, setStatus] = useState(null); // running | done | error
  const [phase, setPhase] = useState(null);
  const [progress, setProgress] = useState(0);
  const [total, setTotal] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const pollRef = useRef(null);

  const stopPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  useEffect(() => stopPoll, []);

  const poll = useCallback((jid) => {
    stopPoll();
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/backtest/strike-philosophy/status/${jid}`);
        setStatus(data.status); setPhase(data.phase);
        setProgress(data.progress || 0); setTotal(data.total || 0);
        if (data.status === "done") { stopPoll(); setResult(data.result); }
        else if (data.status === "error") { stopPoll(); setError(data.error || "Simulation failed"); }
      } catch { /* keep polling */ }
    }, 1200);
  }, []);

  const run = async () => {
    setError(""); setResult(null); setStatus("running"); setPhase("calibrating"); setProgress(0);
    try {
      const { data } = await api.post("/backtest/strike-philosophy/run", {
        ticker: ticker.toUpperCase(), dte, pnt_threshold: pntThreshold / 100,
        short_delta: shortDelta, long_delta: longDelta, wing_width: wingWidth,
        fair_wing: fairWing, exit_rule: exitRule, normalize,
        sim_count: simCount, lookback_years: lookback,
      });
      setJobId(data.job_id);
      if (data.status === "done" && data.cached) {
        const { data: s } = await api.get(`/backtest/strike-philosophy/status/${data.job_id}`);
        setStatus("done"); setResult(s.result);
      } else { poll(data.job_id); }
    } catch (err) {
      setStatus("error"); setError(err.response?.data?.detail || "Could not start simulation");
    }
  };

  const exportCSV = () => {
    if (!jobId) return;
    const base = api.defaults.baseURL || "";
    window.open(`${base}/backtest/strike-philosophy/export/${jobId}`, "_blank");
  };

  const running = status === "running";
  const sum = result?.summary;
  const a = result?.strategy_a || {};
  const b = result?.strategy_b || {};
  const winnerIsA = sum?.winner === "a";

  const assignData = (result?.by_regime || []).map(r => ({
    regime: r.regime?.replace(" Trend", "").replace(" Range", ""),
    "P(NT)": r.a_assign_pct, "Fixed Δ": r.b_assign_pct,
  }));
  const evData = (result?.by_regime || []).map(r => ({
    regime: r.regime?.replace(" Trend", "").replace(" Range", ""),
    "P(NT)": r.a_ev, "Fixed Δ": r.b_ev,
  }));
  const ts = result?.transition_stress;
  const stressAssign = ts ? [
    { phase: "Stable cycles", "P(NT)": ts.a.stable.assign_pct, "Fixed Δ": ts.b.stable.assign_pct },
    { phase: "Transition cycles", "P(NT)": ts.a.transition.assign_pct, "Fixed Δ": ts.b.transition.assign_pct },
  ] : [];
  const stressEv = ts ? [
    { phase: "Stable cycles", "P(NT)": ts.a.stable.ev, "Fixed Δ": ts.b.stable.ev },
    { phase: "Transition cycles", "P(NT)": ts.a.transition.ev, "Fixed Δ": ts.b.transition.ev },
  ] : [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-3" data-testid="strike-philosophy">
      {/* Controls sidebar */}
      <div className="border p-3 rounded-sm space-y-3 h-fit lg:sticky lg:top-3" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }} data-testid="philosophy-controls">
        <div className="flex items-center gap-1.5">
          <Scale className="w-4 h-4" style={{ color: A_COLOR }} />
          <h2 className="text-xs font-bold" style={{ fontFamily: "Chivo, sans-serif" }}>Comparison Inputs</h2>
        </div>
        <div>
          <Label className="text-xs mb-1 block" style={{ color: "var(--hoip-text-secondary)" }}>Ticker</Label>
          <Input data-testid="philosophy-ticker" value={ticker} onChange={(e) => setTicker(e.target.value.toUpperCase())}
            className="bg-[var(--hoip-bg)] border-[var(--hoip-border)] rounded-sm h-8 text-xs" style={{ ...MONO, color: "var(--hoip-text-primary)" }} />
        </div>
        <div className="flex justify-between items-center px-2 py-1.5 rounded-sm" style={{ background: "var(--hoip-bg)" }}>
          <span className="text-xs" style={{ color: "var(--hoip-text-secondary)" }}>DTE</span>
          <div className="flex items-center gap-1.5">
            <select data-testid="dte-select" value={String(dte)} onChange={(e) => setDte(Number(e.target.value))}
              className="h-6 px-1.5 text-xs rounded-sm border outline-none cursor-pointer"
              style={{ ...MONO, background: "var(--hoip-card)", borderColor: "var(--hoip-border)", color: "var(--hoip-text-primary)" }}>
              {DTE_OPTIONS.map(d => <option key={d} value={d} style={{ background: "var(--hoip-card)" }}>{d}</option>)}
            </select>
            <span className="text-[10px]" style={{ ...MONO, color: "var(--hoip-text-tertiary)" }}>· {cyclesPerYear(dte)} cycles/yr</span>
          </div>
        </div>

        <div className="pt-1 border-t" style={{ borderColor: "var(--hoip-divider)" }}>
          <p className="text-[10px] font-semibold mb-2 uppercase tracking-wide" style={{ color: A_COLOR }}>Strategy A · P(NT)</p>
          <Slider label="P(NT) threshold" testId="pnt-threshold-slider" value={pntThreshold} min={25} max={85} step={1} onChange={setPntThreshold} fmt={(v) => `${v}%`} color={thresholdColor(pntThreshold)} />
          <div className="flex gap-1 mt-1.5 text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>
            <span className="flex-1 text-center py-0.5 rounded-sm" style={{ background: pntThreshold <= 45 ? "#EF444422" : "transparent", color: pntThreshold <= 45 ? "#EF4444" : undefined }}>aggressive</span>
            <span className="flex-1 text-center py-0.5 rounded-sm" style={{ background: pntThreshold > 45 && pntThreshold <= 65 ? "#F59E0B22" : "transparent", color: pntThreshold > 45 && pntThreshold <= 65 ? "#F59E0B" : undefined }}>moderate</span>
            <span className="flex-1 text-center py-0.5 rounded-sm" style={{ background: pntThreshold > 65 ? "#10B98122" : "transparent", color: pntThreshold > 65 ? "#10B981" : undefined }}>conservative</span>
          </div>
        </div>

        <div className="pt-1 border-t" style={{ borderColor: "var(--hoip-divider)" }}>
          <p className="text-[10px] font-semibold mb-2 uppercase tracking-wide" style={{ color: B_COLOR }}>Strategy B · Fixed Δ</p>
          <div className="space-y-2.5">
            <Slider label="Short delta" testId="short-delta-slider" value={shortDelta} min={0.15} max={0.35} step={0.01} onChange={setShortDelta} fmt={(v) => v.toFixed(2)} />
            <Slider label="Long delta" testId="long-delta-slider" value={longDelta} min={0.05} max={0.20} step={0.01} onChange={setLongDelta} fmt={(v) => fairWing ? "n/a" : v.toFixed(2)} />
            <label className="flex items-center gap-2 cursor-pointer text-xs" style={{ color: "var(--hoip-text-secondary)" }}>
              <input type="checkbox" data-testid="fair-wing-toggle" checked={fairWing} onChange={(e) => setFairWing(e.target.checked)} className="accent-blue-500" />
              Match A&apos;s wing (fair risk)
            </label>
          </div>
        </div>

        <div className="pt-1 border-t space-y-2.5" style={{ borderColor: "var(--hoip-divider)" }}>
          <NativeSelect label="Exit rule" testId="exit-rule-select" value={exitRule} onChange={setExitRule} options={EXIT_OPTIONS.map(o => ({ value: o.v, label: o.l }))} />
          <NativeSelect label="Wing width" testId="wing-width-select" value={String(wingWidth)} onChange={(v) => setWingWidth(Number(v))} options={WING_OPTIONS.map(w => ({ value: String(w), label: `$${w}` }))} />
          <NativeSelect label="Simulations" testId="sim-count-select" value={String(simCount)} onChange={(v) => setSimCount(Number(v))} options={SIM_OPTIONS.map(s => ({ value: String(s), label: s.toLocaleString() }))} />
          <NativeSelect label="Calibration lookback" testId="lookback-select" value={String(lookback)} onChange={(v) => setLookback(Number(v))} options={LOOKBACK_OPTIONS.map(o => ({ value: String(o.v), label: o.l }))} />
          <label className="flex items-start gap-2 cursor-pointer text-xs pt-1" style={{ color: "var(--hoip-text-secondary)" }}>
            <input type="checkbox" data-testid="normalize-toggle" checked={normalize} onChange={(e) => setNormalize(e.target.checked)} className="mt-0.5 accent-amber-500" />
            <span>Normalize by delta-risk <span className="px-1 rounded-sm text-[9px] font-bold" style={{ background: "#F59E0B22", color: "#F59E0B" }}>EXPERIMENTAL</span><br /><span className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>Scales P(NT) size to equalize expected loss/cycle</span></span>
          </label>
        </div>

        <Button onClick={run} disabled={running} data-testid="philosophy-run-btn"
          className="w-full h-9 bg-white text-black hover:bg-gray-200 rounded-sm text-xs">
          {running ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <Play className="w-3 h-3 mr-1" />}
          {running ? "Simulating…" : "Run Simulation"}
        </Button>
        {result && (
          <Button onClick={exportCSV} variant="outline" data-testid="philosophy-export-btn"
            className="w-full h-8 border-[var(--hoip-border)] text-xs rounded-sm">
            <Download className="w-3 h-3 mr-1" /> Export Results (CSV)
          </Button>
        )}
        {error && <p className="text-xs text-red-500" data-testid="philosophy-error">{error}</p>}
      </div>

      {/* Results */}
      <div className="space-y-3 min-w-0">
        {running && (
          <div className="border p-4 rounded-sm" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }} data-testid="philosophy-progress">
            <div className="flex justify-between text-xs mb-2" style={{ color: "var(--hoip-text-secondary)" }}>
              <span>{phase === "calibrating" ? "Calibrating regime model & return distributions…" : `Simulating path ${progress.toLocaleString()} of ${total.toLocaleString()}…`}</span>
              <span style={MONO}>{total ? Math.round((progress / total) * 100) : 0}%</span>
            </div>
            <div className="h-2 rounded-full overflow-hidden" style={{ background: "var(--hoip-hover)" }}>
              <div className="h-full rounded-full transition-all" style={{ width: `${phase === "calibrating" ? 8 : (total ? (progress / total) * 100 : 0)}%`, background: A_COLOR }} />
            </div>
          </div>
        )}

        {!result && !running && (
          <div className="border p-8 rounded-sm text-center" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}>
            <Scale className="w-8 h-8 mx-auto mb-2" style={{ color: "var(--hoip-text-tertiary)" }} />
            <p className="text-sm font-medium" style={{ color: "var(--hoip-text-primary)" }}>Strike Selection Philosophy Comparison</p>
            <p className="text-xs mt-1 max-w-md mx-auto" style={{ color: "var(--hoip-text-tertiary)" }}>
              A regime-aware Monte Carlo ({SIM_OPTIONS[2].toLocaleString()} paths × 8 cycles) testing whether MarkovLab&apos;s P(NT)/HMM strike selection beats traditional fixed-delta on risk-adjusted returns. Set inputs and run.
            </p>
          </div>
        )}

        {result && (
          <>
            {/* Summary bar */}
            <div className="border rounded-sm p-3 flex flex-wrap items-center gap-x-6 gap-y-2"
              style={{ background: "var(--hoip-card)", borderColor: winnerIsA ? A_COLOR : B_COLOR }} data-testid="philosophy-summary">
              <div className="flex items-center gap-2">
                <Trophy className="w-4 h-4" style={{ color: winnerIsA ? A_COLOR : B_COLOR }} />
                <span className="text-xs font-bold px-2 py-0.5 rounded-sm" data-testid="philosophy-winner-badge"
                  style={{ color: winnerIsA ? A_COLOR : B_COLOR, background: `${winnerIsA ? A_COLOR : B_COLOR}1A` }}>
                  {sum.winner_label} wins on risk-adjusted return
                </span>
              </div>
              <span className="text-xs" style={{ color: "var(--hoip-text-secondary)" }}>ΔSortino <b style={{ ...MONO, color: sum.delta_sortino >= 0 ? "#10B981" : "#EF4444" }}>{sum.delta_sortino >= 0 ? "+" : ""}{sum.delta_sortino}</b></span>
              <span className="text-xs" style={{ color: "var(--hoip-text-secondary)" }}>ΔSharpe <b style={{ ...MONO, color: sum.delta_sharpe >= 0 ? "#10B981" : "#EF4444" }}>{sum.delta_sharpe >= 0 ? "+" : ""}{sum.delta_sharpe}</b></span>
              <span className="text-xs" style={{ color: "var(--hoip-text-secondary)" }}>ΔAssignment <b style={{ ...MONO, color: sum.delta_assignment_pct <= 0 ? "#10B981" : "#EF4444" }}>{sum.delta_assignment_pct > 0 ? "+" : ""}{sum.delta_assignment_pct}%</b></span>
              <span className="text-xs" style={{ color: "var(--hoip-text-secondary)" }}>ΔKelly <b style={{ ...MONO, color: sum.delta_kelly >= 0 ? "#10B981" : "#EF4444" }}>{sum.delta_kelly >= 0 ? "+" : ""}{sum.delta_kelly}</b></span>
              <span className="text-xs ml-auto" style={{ ...MONO, color: "var(--hoip-text-tertiary)" }}>{ticker.toUpperCase()} · {result.dte}DTE · {result.sim_count?.toLocaleString()} paths · spot ${result.spot?.toFixed(2)}</span>
            </div>

            {result.normalized && (
              <div className="border rounded-sm px-3 py-2 text-xs flex items-center gap-2" data-testid="philosophy-normalize-warning"
                style={{ background: "#F59E0B14", borderColor: "#F59E0B", color: "#F59E0B" }}>
                <Scale className="w-3.5 h-3.5" /> Position sizes are scaled to equalize expected loss/cycle — this is <b>not a direct dollar comparison</b>. Use return-on-risk &amp; risk-adjusted ratios.
              </div>
            )}

            {/* Per-regime breakdown + insight panel + sim badge + notes */}
            <RegimeBreakdown result={result} ticker={ticker} />

            {/* Metrics table */}
            <div className="border rounded-sm overflow-hidden" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}>
              <table className="w-full" data-testid="philosophy-metrics-table">
                <thead>
                  <tr className="border-b" style={{ borderColor: "var(--hoip-border)" }}>
                    <th className="text-left text-[10px] font-medium py-2 px-3" style={{ color: "var(--hoip-text-tertiary)" }}>Metric</th>
                    <th className="text-right text-[11px] font-bold py-2 px-3" style={{ color: A_COLOR }}>A · P(NT) Regime</th>
                    <th className="text-right text-[11px] font-bold py-2 px-3" style={{ color: B_COLOR }}>B · Fixed Delta</th>
                  </tr>
                </thead>
                <tbody className="px-1">
                  <tr><td colSpan={3} className="pt-2 px-3 text-[10px] font-semibold uppercase tracking-wide" style={{ color: "var(--hoip-text-tertiary)" }}>Return (primary)</td></tr>
                  <MetricRow label="Return on risk capital" a={a.return_on_risk_pct} b={b.return_on_risk_pct} fmt={pct} help="EV per cycle ÷ defined risk — the headline efficiency metric" />
                  <MetricRow label="Annualized return / cycle" a={a.ann_return_per_cycle_pct} b={b.ann_return_per_cycle_pct} fmt={pct} help="RoR scaled by holding-time compression (365 / avg days held)" />
                  <MetricRow label="Mean annual return %" a={a.mean_annual_return_pct} b={b.mean_annual_return_pct} fmt={pct} help="On defined risk capital, full year of sequential cycles" />
                  <MetricRow label="Mean annual P&L" a={a.mean_annual_pnl} b={b.mean_annual_pnl} fmt={money} />
                  <MetricRow label="Median annual P&L" a={a.median_annual_pnl} b={b.median_annual_pnl} fmt={money} />
                  <MetricRow label="EV per cycle" a={a.ev_per_cycle} b={b.ev_per_cycle} fmt={money} />
                  <tr><td colSpan={3} className="pt-2 px-3 text-[10px] font-semibold uppercase tracking-wide" style={{ color: "var(--hoip-text-tertiary)" }}>Outcomes</td></tr>
                  <MetricRow label="Win rate" a={a.win_rate_pct} b={b.win_rate_pct} fmt={pct} />
                  <MetricRow label="Assignment frequency" a={a.assignment_freq_pct} b={b.assignment_freq_pct} higherBetter={false} fmt={pct} help="% cycles hitting full max loss" />
                  <MetricRow label="Partial assignment freq" a={a.partial_freq_pct} b={b.partial_freq_pct} higherBetter={false} fmt={pct} />
                  <MetricRow label="Avg premium collected" a={a.avg_premium} b={b.avg_premium} fmt={money} />
                  <MetricRow label="Avg max loss" a={a.avg_max_loss} b={b.avg_max_loss} higherBetter={false} fmt={money} />
                  <MetricRow label="Avg days held" a={a.avg_days_held} b={b.avg_days_held} higherBetter={false} fmt={(v) => `${v}d`} help="Lower = faster capital turnover" />
                  <MetricRow label="Cycles / year" a={a.avg_cycles_per_year} b={b.avg_cycles_per_year} fmt={(v) => Number(v).toFixed(1)} />
                  <tr><td colSpan={3} className="pt-2 px-3 text-[10px] font-semibold uppercase tracking-wide" style={{ color: "var(--hoip-text-tertiary)" }}>Risk-adjusted (prefer these)</td></tr>
                  <MetricRow label="Sortino ratio" a={a.sortino} b={b.sortino} fmt={(v) => Number(v).toFixed(3)} help="Downside-deviation only — best for asymmetric BPS returns" />
                  <MetricRow label="Calmar ratio" a={a.calmar} b={b.calmar} fmt={(v) => Number(v).toFixed(3)} help="Mean annual return ÷ max drawdown" />
                  <MetricRow label="Profit factor" a={a.profit_factor} b={b.profit_factor} fmt={(v) => Number(v).toFixed(2)} help="Gross wins ÷ gross losses across all cycles" />
                  <MetricRow label="CVaR 95%" a={a.cvar_95} b={b.cvar_95} fmt={money} help="Expected loss in worst 5% of paths" />
                  <MetricRow label="Kelly fraction" a={a.kelly_fraction} b={b.kelly_fraction} fmt={(v) => Number(v).toFixed(3)} />
                  <MetricRow label="Sharpe ratio (rf 4.5%)" a={a.sharpe} b={b.sharpe} fmt={(v) => Number(v).toFixed(3)} help="Sharpe assumes normal returns. For BPS strategies, prefer Sortino, Calmar and CVaR as primary metrics." />
                  <MetricRow label="Max drawdown (5th pct)" a={a.max_drawdown_p5} b={b.max_drawdown_p5} fmt={money} />
                </tbody>
              </table>
            </div>

            {/* Charts grid */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
              <ChartCard title="Annual P&L Distribution" subtitle="Both strategies overlaid · dashed lines = 5th / 50th / 95th percentile" testId="philosophy-hist-chart">
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={result.histogram}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--hoip-divider)" />
                    <XAxis dataKey="bin_mid" stroke="var(--hoip-text-tertiary)" tick={{ fontSize: 9, fontFamily: "JetBrains Mono" }} tickFormatter={money} />
                    <YAxis stroke="var(--hoip-text-tertiary)" tick={{ fontSize: 9, fontFamily: "JetBrains Mono" }} />
                    <Tooltip contentStyle={{ background: "var(--hoip-card)", border: "1px solid var(--hoip-border)", fontSize: "10px", fontFamily: "JetBrains Mono" }} labelFormatter={money} />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                    <ReferenceLine x={result.pct_markers?.a?.p50} stroke={A_COLOR} strokeDasharray="4 2" />
                    <ReferenceLine x={result.pct_markers?.b?.p50} stroke={B_COLOR} strokeDasharray="4 2" />
                    <Bar dataKey="count_a" name="P(NT)" fill={A_COLOR} opacity={0.65} />
                    <Bar dataKey="count_b" name="Fixed Δ" fill={B_COLOR} opacity={0.65} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard title="Assignment Rate by Regime" subtitle="% of cycles hitting full max loss, per entry regime" testId="philosophy-assign-chart">
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={assignData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--hoip-divider)" />
                    <XAxis dataKey="regime" stroke="var(--hoip-text-tertiary)" tick={{ fontSize: 9, fontFamily: "JetBrains Mono" }} />
                    <YAxis stroke="var(--hoip-text-tertiary)" tick={{ fontSize: 9, fontFamily: "JetBrains Mono" }} tickFormatter={v => `${v}%`} />
                    <Tooltip contentStyle={{ background: "var(--hoip-card)", border: "1px solid var(--hoip-border)", fontSize: "10px", fontFamily: "JetBrains Mono" }} formatter={v => `${v}%`} />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                    <Bar dataKey="P(NT)" fill={A_COLOR} radius={[2, 2, 0, 0]} />
                    <Bar dataKey="Fixed Δ" fill={B_COLOR} radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard title="Expected Value by Regime" subtitle="Mean P&L per cycle ($) — where each approach extracts or destroys value" testId="philosophy-ev-chart">
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={evData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--hoip-divider)" />
                    <XAxis dataKey="regime" stroke="var(--hoip-text-tertiary)" tick={{ fontSize: 9, fontFamily: "JetBrains Mono" }} />
                    <YAxis stroke="var(--hoip-text-tertiary)" tick={{ fontSize: 9, fontFamily: "JetBrains Mono" }} tickFormatter={money} />
                    <Tooltip contentStyle={{ background: "var(--hoip-card)", border: "1px solid var(--hoip-border)", fontSize: "10px", fontFamily: "JetBrains Mono" }} formatter={money} />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                    <ReferenceLine y={0} stroke="var(--hoip-border)" />
                    <Bar dataKey="P(NT)" fill={A_COLOR} radius={[2, 2, 0, 0]} />
                    <Bar dataKey="Fixed Δ" fill={B_COLOR} radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard title="Cumulative P&L Fan Chart" subtitle="Percentile paths (10/25/50/75/90) across the year · solid = P(NT), dashed = Fixed Δ" testId="philosophy-fan-chart">
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={result.fan}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--hoip-divider)" />
                    <XAxis dataKey="cycle" stroke="var(--hoip-text-tertiary)" tick={{ fontSize: 9, fontFamily: "JetBrains Mono" }} tickFormatter={(v) => `${v}%`} label={{ value: "% of year", fontSize: 9, position: "insideBottom", offset: -2, fill: "var(--hoip-text-tertiary)" }} />
                    <YAxis stroke="var(--hoip-text-tertiary)" tick={{ fontSize: 9, fontFamily: "JetBrains Mono" }} tickFormatter={money} />
                    <Tooltip contentStyle={{ background: "var(--hoip-card)", border: "1px solid var(--hoip-border)", fontSize: "10px", fontFamily: "JetBrains Mono" }} formatter={money} labelFormatter={(v) => `${v}% of year`} />
                    <ReferenceLine y={0} stroke="var(--hoip-border)" />
                    {[10, 25, 75, 90].map(p => <Line key={`a${p}`} type="monotone" dataKey={`a_p${p}`} stroke={A_COLOR} strokeWidth={1} opacity={0.35} dot={false} name={`P(NT) p${p}`} />)}
                    <Line type="monotone" dataKey="a_p50" stroke={A_COLOR} strokeWidth={2.5} dot={false} name="P(NT) median" />
                    {[10, 25, 75, 90].map(p => <Line key={`b${p}`} type="monotone" dataKey={`b_p${p}`} stroke={B_COLOR} strokeWidth={1} strokeDasharray="4 2" opacity={0.35} dot={false} name={`FixedΔ p${p}`} />)}
                    <Line type="monotone" dataKey="b_p50" stroke={B_COLOR} strokeWidth={2.5} strokeDasharray="4 2" dot={false} name="Fixed Δ median" />
                  </LineChart>
                </ResponsiveContainer>
              </ChartCard>
            </div>

            {/* Regime-transition stress test */}
            <ChartCard title="P(NT) Edge at Regime Transitions" subtitle="Isolates whether regime conditioning adds value at the moments it's designed for: cycles where the regime CHANGED vs cycles where it held steady" testId="philosophy-stress-chart">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-[10px] mb-1 font-medium" style={{ color: "var(--hoip-text-secondary)" }}>Assignment rate</p>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={stressAssign}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--hoip-divider)" />
                      <XAxis dataKey="phase" stroke="var(--hoip-text-tertiary)" tick={{ fontSize: 9, fontFamily: "JetBrains Mono" }} />
                      <YAxis stroke="var(--hoip-text-tertiary)" tick={{ fontSize: 9, fontFamily: "JetBrains Mono" }} tickFormatter={v => `${v}%`} />
                      <Tooltip contentStyle={{ background: "var(--hoip-card)", border: "1px solid var(--hoip-border)", fontSize: "10px", fontFamily: "JetBrains Mono" }} formatter={v => `${v}%`} />
                      <Legend wrapperStyle={{ fontSize: 10 }} />
                      <Bar dataKey="P(NT)" fill={A_COLOR} radius={[2, 2, 0, 0]} />
                      <Bar dataKey="Fixed Δ" fill={B_COLOR} radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div>
                  <p className="text-[10px] mb-1 font-medium" style={{ color: "var(--hoip-text-secondary)" }}>Expected value per cycle ($)</p>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={stressEv}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--hoip-divider)" />
                      <XAxis dataKey="phase" stroke="var(--hoip-text-tertiary)" tick={{ fontSize: 9, fontFamily: "JetBrains Mono" }} />
                      <YAxis stroke="var(--hoip-text-tertiary)" tick={{ fontSize: 9, fontFamily: "JetBrains Mono" }} tickFormatter={money} />
                      <Tooltip contentStyle={{ background: "var(--hoip-card)", border: "1px solid var(--hoip-border)", fontSize: "10px", fontFamily: "JetBrains Mono" }} formatter={money} />
                      <Legend wrapperStyle={{ fontSize: 10 }} />
                      <ReferenceLine y={0} stroke="var(--hoip-border)" />
                      <Bar dataKey="P(NT)" fill={A_COLOR} radius={[2, 2, 0, 0]} />
                      <Bar dataKey="Fixed Δ" fill={B_COLOR} radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </ChartCard>

            {/* Transition matrix heatmap */}
            <ChartCard title="Empirical Regime Transition Matrix" subtitle={`P(next regime | current regime) over a ${result.dte}-day cycle — how sticky each regime is (rows sum to 1)`} testId="philosophy-transition-heatmap">
              <div className="overflow-x-auto">
                <table className="text-xs" style={MONO}>
                  <thead>
                    <tr>
                      <th className="p-1.5 text-left text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>from \ to</th>
                      {result.regime_labels?.map(r => (
                        <th key={r} className="p-1.5 text-center text-[10px] font-medium" style={{ color: REGIME_COLORS[r] }}>{r.replace(" Trend", "").replace(" Range", "")}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {result.transition_matrix?.map((row, i) => (
                      <tr key={i}>
                        <td className="p-1.5 text-[10px] font-medium" style={{ color: REGIME_COLORS[result.regime_labels[i]] }}>{result.regime_labels[i]?.replace(" Trend", "").replace(" Range", "")}</td>
                        {row.map((v, j) => (
                          <td key={j} className="p-2 text-center font-medium" style={{ background: `rgba(59,130,246,${(v * 0.85).toFixed(3)})`, color: v > 0.55 ? "#fff" : "var(--hoip-text-primary)" }}>
                            {(v * 100).toFixed(0)}%
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </ChartCard>

            {/* Regime distributions */}
            <ChartCard title="Regime-Conditional 35-Day Return Distributions" subtitle="Student-t fit per regime (empirical fallback when thin) — the engine samples these, not a single Gaussian" testId="philosophy-regime-dist-table">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b" style={{ borderColor: "var(--hoip-border)" }}>
                      {["Regime", "Freq", "Samples", "Mean 35d", "Std 35d", "Skew", "Real. vol", "t-df", "Method"].map(h => (
                        <th key={h} className="text-right text-[10px] font-medium py-1.5 px-2" style={{ color: "var(--hoip-text-tertiary)" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {result.regime_distributions?.map((d, i) => (
                      <tr key={i} className="border-b" style={{ borderColor: "var(--hoip-divider)" }}>
                        <td className="py-1.5 px-2 text-xs font-medium text-left" style={{ color: REGIME_COLORS[d.regime] }}>{d.regime}</td>
                        <td className="py-1.5 px-2 text-right text-xs" style={MONO}>{pct(d.freq_pct)}</td>
                        <td className="py-1.5 px-2 text-right text-xs" style={MONO}>{d.n_samples}</td>
                        <td className="py-1.5 px-2 text-right text-xs" style={{ ...MONO, color: d.mean_35d_pct >= 0 ? "#10B981" : "#EF4444" }}>{d.mean_35d_pct != null ? `${d.mean_35d_pct}%` : "—"}</td>
                        <td className="py-1.5 px-2 text-right text-xs" style={MONO}>{d.std_35d_pct != null ? `${d.std_35d_pct}%` : "—"}</td>
                        <td className="py-1.5 px-2 text-right text-xs" style={MONO}>{d.skew ?? "—"}</td>
                        <td className="py-1.5 px-2 text-right text-xs" style={MONO}>{d.realized_vol_annual_pct}%</td>
                        <td className="py-1.5 px-2 text-right text-xs" style={MONO}>{d.t_df ?? "—"}</td>
                        <td className="py-1.5 px-2 text-right text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>{d.method?.replace("_", " ")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </ChartCard>

            {/* Selected strikes per regime */}
            <ChartCard title="Selected Strikes per Regime" subtitle={`Spot $${result.spot?.toFixed(2)} · A walks OTM to the P(NT) threshold (further in Volatile/Bearish) · B is fixed-delta`} testId="philosophy-strikes-table">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b" style={{ borderColor: "var(--hoip-border)" }}>
                      {["Regime", "A short", "A long", "A credit", "A P(NT)", "B short", "B long", "B credit", "B Δ"].map(h => (
                        <th key={h} className="text-right text-[10px] font-medium py-1.5 px-2" style={{ color: "var(--hoip-text-tertiary)" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {result.strikes?.map((s, i) => (
                      <tr key={i} className="border-b" style={{ borderColor: "var(--hoip-divider)" }}>
                        <td className="py-1.5 px-2 text-xs font-medium text-left" style={{ color: REGIME_COLORS[s.regime] }}>{s.regime}</td>
                        <td className="py-1.5 px-2 text-right text-xs" style={MONO}>${s.a_short}</td>
                        <td className="py-1.5 px-2 text-right text-xs" style={MONO}>${s.a_long}</td>
                        <td className="py-1.5 px-2 text-right text-xs" style={MONO}>${s.a_credit}</td>
                        <td className="py-1.5 px-2 text-right text-xs" style={{ ...MONO, color: A_COLOR }}>{s.a_pnt}%</td>
                        <td className="py-1.5 px-2 text-right text-xs" style={MONO}>${s.b_short}</td>
                        <td className="py-1.5 px-2 text-right text-xs" style={MONO}>${s.b_long}</td>
                        <td className="py-1.5 px-2 text-right text-xs" style={MONO}>${s.b_credit}</td>
                        <td className="py-1.5 px-2 text-right text-xs" style={{ ...MONO, color: B_COLOR }}>{s.b_delta}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </ChartCard>

            <p className="text-[10px] leading-relaxed px-1" style={{ color: "var(--hoip-text-tertiary)" }}>
              Regime sequences are sampled via the empirical Markov transition matrix (preserving regime clustering), 35-day returns drawn from each regime&apos;s fitted distribution, and BPS premiums approximated via Black-Scholes on per-regime realized vol (Strategy A) or overall ATM vol (Strategy B). For educational/research purposes only — not investment advice.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
